import React, { CSSProperties } from 'react';
import { Select } from 'antd';
import { IDivisionSelectorContext, Option } from './interface';
import { useSessionStore } from '../../../context/session';
import { AreaCollection } from '../../../utils/area_collection';
import { SEARCH_EMPTY_SELECT } from '../../../constant';

/**
 * 街区・フロア・区画選択ボックスの共通プロパティ
 */
export type BaseSelectorProp = {
  /** 選択ボックスのCSSクラス */
  className?: string;
  /** 選択ボックスのスタイル */
  selectStyle?: CSSProperties
  /** 項目の非活性 */
  disable?: boolean
};

export type DivisionSelectorProp = BaseSelectorProp;
export type FloorSelectorProp = BaseSelectorProp;
export type BlockSelectorProp = BaseSelectorProp;

type ContextFunc = () => IDivisionSelectorContext;

const defaultOption: Option = { value: 0, label: SEARCH_EMPTY_SELECT } as const;

/**
 * 街区選択ボックスを作成するセレクタのファクトリクラス
 * DivisionSelectorProvider で管理された街区情報を表示・設定する
 */
export function DivisionSelectorFunc(useContext: ContextFunc): React.FC<DivisionSelectorProp> {
  return ({ className, selectStyle, disable = false }) => {
    const { divisionId, update } = useContext();
    const areaInfo = new AreaCollection(useSessionStore((selector) => selector.areasInfo));
    const options: Option[] = [defaultOption, ...areaInfo.divisions().list.map((d) => ({ value: d.id, label: d.name }))];

    return (
          <Select
              disabled={disable}
              style={selectStyle}
              className={className}
              value={divisionId}
              options={options}
              onChange={(value) => {
                update({ divisionId: value });
              }}
          />
    );
  };
}

/**
 * フロア選択ボックスを作成するセレクタのファクトリクラス
 * DivisionSelectorProvider で管理されたフロア情報を表示・設定し、DivisionSelectorFuncとも連動して値が変化する
 */
export function FloorSelectorFunc(useContext: ContextFunc): React.FC<FloorSelectorProp> {
  return ({ className, selectStyle, disable = false }) => {
    const { divisionId, floorId, update } = useContext();
    const areaInfo = new AreaCollection(useSessionStore((selector) => selector.areasInfo));
    const options: Option[] = [defaultOption, ...areaInfo.floors(divisionId).list.map((d) => ({ value: d.id, label: d.name }))];

    return (
            <Select
                disabled={disable || divisionId === 0}
                style={selectStyle}
                className={className}
                value={floorId}
                options={options}
                onChange={(value) => {
                  update({ floorId: value });
                }}
            />
    );
  };
}

/**
 * 区画選択ボックスを作成するセレクタのファクトリクラス
 * DivisionSelectorProvider で管理されたフロア情報を表示・設定し、DivisionSelectorFunc や FloorSelectorFuncとも連動して値が変化する
 */
export function BlockSelectorFunc(useContext: ContextFunc): React.FC<BlockSelectorProp> {
  return ({ className, selectStyle, disable = false }) => {
    const { divisionId, floorId, blockId, update } = useContext();
    const areaInfo = new AreaCollection(useSessionStore((selector) => selector.areasInfo));
    const options: Option[] = [defaultOption, ...areaInfo.blocks(divisionId, floorId).list.map((d) => ({ value: d.id, label: d.name }))];

    return (
            <Select
                disabled={disable || floorId === 0}
                style={selectStyle}
                className={className}
                value={blockId}
                options={options}
                onChange={(value) => {
                  update({ blockId: value });
                }}
            />
    );
  };
}
